import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  InventorySettingsSummary,
  PaginatedResult,
  StagnantStockReport,
  ProductBatchSummary,
  ProductCategorySummary,
  StockAlerts,
  StockAdjustmentReasonSummary,
  StockLevel,
  StockTransactionSummary,
  StockTransactionType,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';
import { catalogKeys } from '@/modules/catalog/api/catalog.api';

export interface StockListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  lowStockOnly?: boolean;
  outOfStockOnly?: boolean;
}

export interface BatchInput {
  batchNumber: string;
  expiryDate?: string;
  manufacturedDate?: string;
  supplierReference?: string;
}

export const inventoryKeys = {
  all: ['inventory'] as const,
  categories: () => [...inventoryKeys.all, 'categories'] as const,
  stock: () => [...inventoryKeys.all, 'stock'] as const,
  stockList: (params: StockListParams) => [...inventoryKeys.stock(), 'list', params] as const,
  variant: (variantId: string) => [...inventoryKeys.stock(), 'variant', variantId] as const,
  batches: (variantId: string) => [...inventoryKeys.stock(), 'batches', variantId] as const,
  alerts: (windowDays: number) => [...inventoryKeys.all, 'alerts', windowDays] as const,
  transactions: (variantId: string | undefined, type: string | undefined) =>
    [...inventoryKeys.all, 'transactions', variantId ?? 'all', type ?? 'all'] as const,
};

function toParams(params: StockListParams): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
  };
  if (params.search?.trim()) out.search = params.search.trim();
  if (params.lowStockOnly) out.lowStockOnly = true;
  if (params.outOfStockOnly) out.outOfStockOnly = true;
  return out;
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export function useCategoriesQuery() {
  return useQuery({
    queryKey: inventoryKeys.categories(),
    queryFn: () =>
      api.get<PaginatedResult<ProductCategorySummary>>('/categories', {
        params: { pageSize: 100, isActive: true },
      }),
    // Categories change rarely; refetching on every form open is wasted work.
    staleTime: 5 * 60_000,
  });
}

/** Every category for the management screen, in use or not, optionally searched by name. */
export function useCategoryListQuery(params: { search: string; isActive: boolean | null }) {
  return useQuery({
    queryKey: [...inventoryKeys.categories(), 'manage', params] as const,
    queryFn: () =>
      api.get<PaginatedResult<ProductCategorySummary>>('/categories', {
        params: {
          pageSize: 100,
          ...(params.search.trim() ? { search: params.search.trim() } : {}),
          ...(params.isActive === null ? {} : { isActive: params.isActive }),
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useCreateCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { name: string; nameAr?: string; parentId?: string }) =>
      api.post<ProductCategorySummary>('/categories', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.categories() });
    },
  });
}

/** Renames, re-parents, or takes a category out of use (or back); nothing is deleted. */
export function useUpdateCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string;
      name?: string;
      nameAr?: string | null;
      parentId?: string | null;
      isActive?: boolean;
    }) => api.patch<ProductCategorySummary>(`/categories/${id}`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.categories() });
      // Product lists show the category name, so a rename must reach them too.
      void queryClient.invalidateQueries({ queryKey: catalogKeys.products() });
    },
  });
}

// ---------------------------------------------------------------------------
// Stock
// ---------------------------------------------------------------------------

export function useStockQuery(params: StockListParams) {
  return useQuery({
    queryKey: inventoryKeys.stockList(params),
    queryFn: () => api.get<PaginatedResult<StockLevel>>('/stock', { params: toParams(params) }),
    placeholderData: (previous) => previous,
  });
}

export function useVariantStockQuery(variantId: string | undefined) {
  return useQuery({
    queryKey: inventoryKeys.variant(variantId ?? ''),
    queryFn: () =>
      api.get<{ levels: StockLevel[]; batches: ProductBatchSummary[] }>(
        `/stock/variant/${variantId ?? ''}`,
      ),
    enabled: Boolean(variantId),
  });
}

export function useBatchesQuery(variantId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.batches(variantId ?? ''),
    queryFn: () => api.get<ProductBatchSummary[]>(`/stock/variant/${variantId ?? ''}/batches`),
    enabled: Boolean(variantId) && enabled,
  });
}

export function useStockAlertsQuery(windowDays = 90) {
  return useQuery({
    queryKey: inventoryKeys.alerts(windowDays),
    queryFn: () =>
      api.get<StockAlerts>('/stock/alerts', { params: { expiryWindowDays: windowDays } }),
  });
}

export function useStockTransactionsQuery(params: {
  variantId?: string;
  type?: StockTransactionType | '';
  pageSize?: number;
}) {
  return useQuery({
    queryKey: inventoryKeys.transactions(params.variantId, params.type || undefined),
    queryFn: () =>
      api.get<PaginatedResult<StockTransactionSummary>>('/stock/transactions', {
        params: {
          pageSize: params.pageSize ?? 25,
          ...(params.variantId ? { variantId: params.variantId } : {}),
          ...(params.type ? { type: params.type } : {}),
        },
      }),
  });
}

/** Every movement invalidates stock, alerts, the ledger and the product it belongs to. */
function useMovementInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    void queryClient.invalidateQueries({ queryKey: catalogKeys.products() });
  };
}

export interface ReceiveStockPayload {
  variantId: string;
  quantity: number;
  batch?: BatchInput;
  reason?: string;
}

/** Books a delivery in, once: a retry after a lost response carries the same idempotency key and is answered with the first booking. */
export function useReceiveStockMutation() {
  const invalidate = useMovementInvalidation();

  return useIdempotentMutation<StockLevel, ReceiveStockPayload>(
    (payload, config) => api.post<StockLevel>('/stock/receive', payload, config),
    { onSuccess: invalidate },
  );
}

export function useIssueStockMutation() {
  const invalidate = useMovementInvalidation();

  return useMutation({
    mutationFn: (payload: {
      variantId: string;
      quantity: number;
      batchId?: string;
      reason?: string;
    }) => api.post<StockLevel>('/stock/issue', payload),
    onSuccess: invalidate,
  });
}

export function useAdjustStockMutation() {
  const invalidate = useMovementInvalidation();

  return useMutation({
    mutationFn: (payload: {
      variantId: string;
      batchId?: string;
      /** Exactly one of these two. Signed change, or the figure on the shelf. */
      quantityDelta?: number;
      quantityOnHand?: number;
      reasonId: string;
      note?: string;
    }) => api.post<StockLevel>('/stock/adjust', payload),
    onSuccess: invalidate,
  });
}

/** Reason codes an adjustment may cite. Rarely change, so they are cached for the session. */
export function useAdjustmentReasonsQuery() {
  return useQuery({
    queryKey: [...inventoryKeys.all, 'adjustment-reasons'] as const,
    queryFn: () => api.get<StockAdjustmentReasonSummary[]>('/inventory/adjustment-reasons'),
    staleTime: 5 * 60_000,
  });
}

export function useInventorySettingsQuery() {
  return useQuery({
    queryKey: [...inventoryKeys.all, 'settings'] as const,
    queryFn: () => api.get<InventorySettingsSummary>('/inventory/settings'),
    staleTime: 5 * 60_000,
  });
}

export function useSetReorderPointMutation(variantId: string) {
  const invalidate = useMovementInvalidation();

  return useMutation({
    mutationFn: (payload: { reorderPoint?: number; reorderQuantity?: number }) =>
      api.patch<StockLevel>(`/stock/variant/${variantId}/reorder-point`, payload),
    onSuccess: invalidate,
  });
}

/** Stock that has stopped moving at the active branch, by the tenant's own thresholds. */
export function useSlowMovingQuery() {
  return useQuery({
    queryKey: [...inventoryKeys.all, 'slow-moving'] as const,
    queryFn: () => api.get<StagnantStockReport>('/inventory/slow-moving'),
  });
}

export function useDeadStockQuery() {
  return useQuery({
    queryKey: [...inventoryKeys.all, 'dead-stock'] as const,
    queryFn: () => api.get<StagnantStockReport>('/inventory/dead-stock'),
  });
}
